import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { RecipeList, recipeLineageCount } from './RecipeList.js';

export function FlatFileRecipesTab({ ehrId }: { ehrId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['flat-file-recipes', ehrId],
    queryFn: () => api.listEhrFlatFileRecipes(ehrId),
    refetchInterval: 30_000,
  });
  const recipes = data?.recipes ?? [];
  const active = recipes.filter((r) => r.status === 'accepted').length;
  const held = recipes.filter((r) => r.status === 'pending_review').length;
  const shapes = recipeLineageCount(recipes);

  return (
    <div className="p-6 max-w-[1000px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-semibold text-text-primary">Flat-file recipes</div>
          <div className="text-[11px] text-text-dimmed mt-0.5">
            Reusable per-shape mappings, active on authoring — a matching drop imports deterministically (no AI).
            The health check is the gate, not human review; you can still inspect or hold any recipe.
          </div>
        </div>
        {recipes.length > 0 && (
          <div className="text-[11px] text-text-dimmed">
            {shapes} shape{shapes !== 1 ? 's' : ''} · {active} active{held > 0 ? ` · ${held} held` : ''}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-text-dimmed">Loading…</div>
      ) : recipes.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
          <div className="text-sm text-text-secondary">No recipes yet for this EHR.</div>
          <div className="text-[11px] text-text-dimmed mt-1">
            Drop files in a session and author a mapping — it’s saved here and reused automatically on the next matching drop.
          </div>
        </div>
      ) : (
        <RecipeList recipes={recipes} onChanged={() => void qc.invalidateQueries({ queryKey: ['flat-file-recipes', ehrId] })} />
      )}
    </div>
  );
}
